import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { File, User, Users } from "lucide-react";
import { SimpleTooltip } from "../../components/ui/tooltip/index.js";
import { CampaignGroupsPopover, campaignSourceChipClass } from "./CampaignGroupsPopover.js";

function SourceCountChip({
  icon,
  label,
  tooltip,
}: {
  icon: ReactNode;
  label: string;
  tooltip: string;
}): React.JSX.Element {
  return (
    <SimpleTooltip content={tooltip}>
      <span className={campaignSourceChipClass} tabIndex={0}>
        {icon}
        {label}
      </span>
    </SimpleTooltip>
  );
}

export function CampaignAudienceSummary({
  sourceCounts,
  groups,
}: {
  sourceCounts: { contacts: number; groups: number; csv: number } | null;
  groups: ReadonlyArray<{ id: number; name: string }>;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  if (!sourceCounts) return null;
  const beforeDedup = t("campaigns.sources.beforeDedup");

  return (
    <div
      className="flex min-w-0 max-w-full flex-wrap items-center gap-1.5"
      data-testid="campaign-audience-source-chips"
    >
      <SourceCountChip
        icon={<User size={11} className="shrink-0 text-muted" aria-hidden="true" />}
        label={t("campaigns.sources.contactsChip", { count: sourceCounts.contacts })}
        tooltip={beforeDedup}
      />
      {sourceCounts.groups > 0 && groups.length > 0 ? (
        <CampaignGroupsPopover
          groups={groups}
          count={sourceCounts.groups}
          testId="campaign-audience-groups-trigger"
        />
      ) : (
        <SourceCountChip
          icon={<Users size={11} className="shrink-0 text-muted" aria-hidden="true" />}
          label={t("campaigns.sources.groupsChip", { count: sourceCounts.groups })}
          tooltip={beforeDedup}
        />
      )}
      <SourceCountChip
        icon={<File size={11} className="shrink-0 text-muted" aria-hidden="true" />}
        label={t("campaigns.sources.csvChip", { count: sourceCounts.csv })}
        tooltip={beforeDedup}
      />
    </div>
  );
}
